/**
 *
 * TreatmentFlow
 *
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import * as PropTypes from "prop-types";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import injectSaga from "utils/injectSaga";
import injectReducer from "utils/injectReducer";
import {
  makeSelectTreatmentFlowCurrentConfig,
  makeSelectTreatmentFlowInitialLoading,
  makeSelectTreatmentFlowTypes,
} from "./selectors";
import reducer from "./reducer";
import saga from "./saga";
import { makeStyles } from "@material-ui/core/styles";
import TreatmentHeader from "containers/TreatmentFlow/TreatmentHeader";
import {
  getActiveProductPlan,
  getProductConfiguration,
  getProductTypes,
  reset,
} from "containers/TreatmentFlow/actions";
import LoadingAnimation from "basicComponents/LoadingAnimation/LoadingAnimation";
import ProductList from "containers/TreatmentFlow/ProductList";

const useStyles = makeStyles(theme => ({
  container: {
    border: "solid 1px #454546",
    borderRadius: "10px",
    width: "100%",
  },
  loadingContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

function TreatmentFlow({
  productPlan = {},
  onChangeProductPlan,
  onGetProductTypes,
  activeOrderId,
  onChangeActiveOrderId,
  productTypes,
  initialLoading,
  onEdit,
  onDelete,
  onGetProductConfiguration,
  currentConfig = {},
  onReset,
}) {
  const classes = useStyles();

  useEffect(
    () => {
      onGetProductTypes();

      return () => {
        onReset();
      };
    },
    [onGetProductTypes, onReset],
  );

  useEffect(
    () => {
      if (
        currentConfig &&
        currentConfig.id &&
        productPlan.productTypeConfigurationId !== currentConfig.id
      ) {
        const pp = {
          ...(productPlan || {}),
          orders: productPlan.orders || [],
          productTypeConfigurationId: currentConfig.id || 0,
          productTypeConfiguration: {
            ...currentConfig,
          },
        };

        onChangeProductPlan(pp);

        onChangeActiveOrderId(
          (pp.productTypeConfiguration.products || []).find(
            ({ active = false, status = 1 }) => active === true && status === 1,
          ).order,
        );
      }
    },
    [
      currentConfig,
      currentConfig.id,
      onChangeActiveOrderId,
      onChangeProductPlan,
      productPlan,
      productPlan.id,
    ],
  );

  useEffect(
    () => {
      if (currentConfig.id === null) {
        onChangeProductPlan({});
        onChangeActiveOrderId(0);
      }
    },
    [currentConfig.id, onChangeActiveOrderId, onChangeProductPlan],
  );

  const handleTypeChange = useCallback(
    (lowerId, upperId) => {
      if (!lowerId || !upperId) {
        return;
      }

      onGetProductConfiguration(upperId, lowerId);
    },
    [onGetProductConfiguration],
  );

  const canChangeOrder = useMemo(
    () =>
      (productPlan.orders || []).filter(({ status }) => status > 1).length > 0,
    [productPlan.orders],
  );

  const products = useMemo(
    () => {
      if (
        productPlan.productTypeConfiguration &&
        productPlan.productTypeConfiguration.products
      ) {
        const prods = productPlan.productTypeConfiguration.products;
        prods.sort((a,b) => a.order - b.order)
        const orders = productPlan.orders || [];

        return prods.map(product => {
          const linkedOrder =
            orders.find(({ order }) => order === product.order) || {};

          return {
            ...linkedOrder,
            ...product,
            id: linkedOrder.id || product.id,
            active: product.active || linkedOrder.active,
          };
        });
      }

      return [];
    },
    [productPlan.orders, productPlan.productTypeConfiguration],
  );

  const handleProductsChange = useCallback(
    newProducts => {
      if (products.orders && products.orders.length > 0) {
        onChangeProductPlan({
          ...productPlan,
          orders: newProducts,
        });
      } else {
        onChangeProductPlan({
          ...productPlan,
          productTypeConfiguration: {
            ...productPlan.productTypeConfiguration,
            products: newProducts,
          },
        });
      }
    },
    [onChangeProductPlan, productPlan, products.orders],
  );

  if (initialLoading) {
    return (
      <div className={classes.loadingContainer}>
        <LoadingAnimation />
      </div>
    );
  }

  const { productTypeConfiguration = {} } = productPlan;

  return (
    <div className={classes.container}>
      <TreatmentHeader
        onChange={handleTypeChange}
        disabled={canChangeOrder}
        upperProductTypeId={productTypeConfiguration.upperProductTypeId}
        lowerProductTypeId={productTypeConfiguration.lowerProductTypeId}
        productTypes={productTypes}
      />
      <ProductList
        products={products}
        onChangeProducts={handleProductsChange}
        currentProductId={activeOrderId}
        onChangeCurrentProductId={onChangeActiveOrderId}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
}

TreatmentFlow.propTypes = {
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onReset: PropTypes.func.isRequired,
  onGetProductTypes: PropTypes.func.isRequired,
  initialLoading: PropTypes.bool,
  onGetProductConfiguration: PropTypes.func.isRequired,
  productTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      description: PropTypes.string,
    }),
  ),
  currentConfig: PropTypes.shape({
    id: PropTypes.number,
    productTypeId: PropTypes.number,
    lowerProductTypeId: PropTypes.number,
    lowerProductTypeName: PropTypes.string,
    upperProductTypeId: PropTypes.number,
    upperProductTypeName: PropTypes.string,
    products: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        fase: PropTypes.string,
        order: PropTypes.number,
        lowerProductId: PropTypes.number,
        lowerProductName: PropTypes.string,
        upperProductId: PropTypes.number,
        upperProductName: PropTypes.string,
        active: PropTypes.bool,
      }),
    ),
  }),
};

const mapStateToProps = createStructuredSelector({
  productTypes: makeSelectTreatmentFlowTypes(),
  initialLoading: makeSelectTreatmentFlowInitialLoading(),
  currentConfig: makeSelectTreatmentFlowCurrentConfig(),
});

function mapDispatchToProps(dispatch) {
  return {
    onGetProductTypes: () => dispatch(getProductTypes()),
    onGetActiveProductPlan: patientId =>
      dispatch(getActiveProductPlan(patientId)),
    onGetProductConfiguration: (upperId, lowerId) =>
      dispatch(getProductConfiguration(upperId, lowerId)),
    onReset: () => dispatch(reset()),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

const withReducer = injectReducer({ key: "treatmentFlow", reducer });
const withSaga = injectSaga({ key: "treatmentFlow", saga });

export default compose(
  withReducer,
  withSaga,
  withConnect,
)(TreatmentFlow);
